/**
 * 「试试问我」首屏 3 张卡 + 输入框上方快捷按钮共用的素材表。
 *
 * 数据职责分工 (产品 2026-06-22):
 *  - 素材 (src/icon/label): 前端常量, 不随团队变 (本文件 ASSETS 表)
 *  - query / 可见 key: 后端 /preset_questions.onboarding_cards 实时下发
 *      (xiaohei-claw internal/tools/onboarding_cards.go ResolveOnboardingCards)
 *
 * 后端按团队最近 100 张水印里出现最多的 title 个性化 ledger query
 * (例: 用「施工区域」的团队收到 ...按相同施工区域...; 用「地点」的收到 ...按相同地点...)。
 *
 * 一份数据两处用：
 *  - OnboardingCarousel.tsx 用 src + cardLabel 渲染图卡 + 点击发后端给的 query
 *  - OnboardingShortcutRow.tsx 用 shortcutLabel/ShortcutIcon 渲染输入框上方一排小按钮 + 同一份 query
 */
import React from 'react'

// 轮播卡图片改为 import（由 Vite 接管、产出带 base 的 CDN URL）。
// 此前硬编码 '/assets/cardN-v2.png' 字面量不被 Vite 重写，
// 部署后源站只剩 index.html → 解析到源站 404 裂图（RFCT-16）。
import card1 from '../images/onboardingCard/card1-v2.png'
import card2 from '../images/onboardingCard/card2-v2.png'
import card3 from '../images/onboardingCard/card3-v2.png'

export type OnboardingCardKey = 'ranking' | 'ledger' | 'attendance'

/** 后端 /preset_questions.onboarding_cards 数组元素形态 */
export interface OnboardingCardBackendItem {
  key: string
  query: string
}

/** 本地素材, 按 key 与后端响应配对 */
interface OnboardingCardAssets {
  key: OnboardingCardKey
  /** 轮播卡图片 (import 自 ../images/，Vite 产出带 base 的 CDN URL) */
  src: string
  /** 轮播卡 img alt + 内部追踪用 */
  cardLabel: string
  /** 输入框上方横排按钮的可见标题 */
  shortcutLabel: string
  /**
   * 输入框上方横排按钮的 icon (产品 2026-06-22 指定 SVG, 16x16, 描边色 #7C7A75)。
   * 2026-06-23 产品(刘乾隆)要求去图标精简, OnboardingShortcutRow 已不再渲染;
   * 字段与下方 3 个 SVG 定义暂保留, 便于后续按需恢复。
   */
  ShortcutIcon: React.FC
}

/** 合并后端 query 与本地素材后的完整一张卡 */
export interface OnboardingCardItem extends OnboardingCardAssets {
  query: string
}

// ─── 内联 SVG 图标 (产品 2026-06-22 给的设计稿) ────────────────────────
// 16x16 viewBox, 描边色 #7C7A75, 无填充

const IconRanking: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.66732 6H1.33398V14H5.66732V6Z"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.99935 2H5.66602V14H9.99935V2Z"
      stroke="#7C7A75"
      strokeLinejoin="round"
    />
    <path
      d="M14.3333 8.66675H10V14.0001H14.3333V8.66675Z"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconLedger: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.6673 2H2.33398C1.7817 2 1.33398 2.44772 1.33398 3V13C1.33398 13.5523 1.7817 14 2.33398 14H13.6673C14.2196 14 14.6673 13.5523 14.6673 13V3C14.6673 2.44772 14.2196 2 13.6673 2Z"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1.33398 4.66675H14.6673"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.66602 8H11.9993"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.66602 10.6667H11.9993"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 8H4.66667"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 10.6667H4.66667"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const IconAttendance: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.666 1.33325H3.33268C2.96449 1.33325 2.66602 1.63173 2.66602 1.99992V13.9999C2.66602 14.3681 2.96449 14.6666 3.33268 14.6666H12.666C13.0342 14.6666 13.3327 14.3681 13.3327 13.9999V1.99992C13.3327 1.63173 13.0342 1.33325 12.666 1.33325Z"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.66602 5.33325H11.3327"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.66602 8H11.3327"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.66602 10.6667H11.3327"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 4V12"
      stroke="#7C7A75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * 3 条 onboarding 入口的本地素材。
 * 后端不会下发 src/icon/label 这种纯展示资源, 前端按 key 匹配。
 */
const ASSETS: OnboardingCardAssets[] = [
  {
    key: 'ranking',
    src: card1,
    cardLabel: '本周拍照排行',
    shortcutLabel: '看本周拍照排行',
    ShortcutIcon: IconRanking
  },
  {
    key: 'ledger',
    src: card2,
    cardLabel: '导出照片台账表',
    shortcutLabel: '导出照片台账表',
    ShortcutIcon: IconLedger
  },
  {
    key: 'attendance',
    src: card3,
    cardLabel: '导出考勤台账',
    shortcutLabel: '导出考勤表',
    ShortcutIcon: IconAttendance
  }
]

const ASSETS_BY_KEY: Record<string, OnboardingCardAssets> = ASSETS.reduce(
  (acc, a) => {
    acc[a.key] = a
    return acc
  },
  {} as Record<string, OnboardingCardAssets>
)

/**
 * 合并后端 onboarding_cards 与本地素材表, 输出最终可渲染列表。
 * 后端给出的 key 顺序决定渲染顺序; 找不到本地素材的 key (= 前端老版本不认识的新卡) 安全跳过。
 */
export function mergeOnboardingCards(
  backendCards: OnboardingCardBackendItem[]
): OnboardingCardItem[] {
  const out: OnboardingCardItem[] = []
  for (const c of backendCards) {
    const a = ASSETS_BY_KEY[c.key]
    if (!a) continue
    out.push({ ...a, query: c.query })
  }
  return out
}
